import type { ComponentChildren } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { X, Loader2, CheckCircle, Unplug } from 'lucide-preact'
import { useSalesforceAuth } from '@/hooks/useSalesforceAuth'
import { IntegrationStatusBadge, IntegrationErrorDetail } from '../components/health'
import { ScopeList } from '../components/ScopeList'
import { CrmSlotTakenNotice } from '../components/CrmSlotTakenNotice'
import { crmBlockedDetail, crmBlockedHeadline } from '../crmSlot'
import { IntegrationProvider } from '@/types/integrations'

interface SalesforceConnectPanelProps {
    isOpen: boolean
    /** The CRM already holding the company's slot, when there is one. */
    blockedByCrm?: string | null
    clientCompanyId: string
    onClose: () => void
    onComplete: () => void
}

/**
 * Slide-in panel for the Salesforce OAuth connection. Salesforce is OAuth-based (like HubSpot)
 * so it gets a dedicated connect/status panel instead of the generic API-key IntegrationPanel.
 * v1 is connect / status / disconnect only — no field-mapping wizard yet (that lands with the
 * contact-field sync work).
 */
export function SalesforceConnectPanel({ isOpen, blockedByCrm, clientCompanyId, onClose, onComplete }: SalesforceConnectPanelProps) {
    const {
        isConnected, connectionId, status, scopes, instanceUrl,
        isSoftFailure, consecutiveFailureCount, recentErrors, aiSummary,
        connectedAtUtc, lastOutboundSyncUtc, lastInboundSyncUtc,
        isLoading, isConnecting, connect, disconnect,
    } = useSalesforceAuth(clientCompanyId, isOpen)

    // Holds an on-demand-generated AI summary so reopening within the same session reuses it.
    const [aiSummaryOverride, setAiSummaryOverride] = useState<string | null>(null)
    useEffect(() => {
        if (!isOpen) setAiSummaryOverride(null)
    }, [isOpen])

    if (!isOpen) return null

    const assetBase = (import.meta as ImportMeta).env?.VITE_ASSETS_BASE_URL?.replace(/\/$/, '') || ''
    const blockedBy = !isConnected && blockedByCrm ? blockedByCrm : null
    const headerCaption = isLoading
        ? 'Loading…'
        : isConnected ? 'Connection overview'
        : blockedBy ? 'Unavailable'
        : 'Connect your CRM'

    const handleDisconnect = async () => {
        await disconnect()
        onComplete()
    }

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose} />

            {/* Panel */}
            <div className="fixed inset-y-0 right-0 z-50 w-full md:w-1/2 bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-slide-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <img src={`${assetBase}/assets/integrations/salesforce-logo.svg`} alt="Salesforce" className="w-8 h-8" />
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Salesforce Integration</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{headerCaption}</p>
                        </div>
                    </div>
                    <button type="button" aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                        </div>
                    ) : isConnected ? (
                        <div className="space-y-6">
                            <ConnectionStatusCard
                                instanceUrl={instanceUrl}
                                scopes={scopes}
                                connectedAtUtc={connectedAtUtc}
                                lastOutboundSyncUtc={lastOutboundSyncUtc}
                                lastInboundSyncUtc={lastInboundSyncUtc}
                                healthStatus={status}
                                isSoftFailure={isSoftFailure}
                            />
                            {recentErrors.length > 0 && (
                                <IntegrationErrorDetail
                                    status={status}
                                    isSoftFailure={isSoftFailure}
                                    consecutiveFailureCount={consecutiveFailureCount}
                                    recentErrors={recentErrors}
                                    aiSummary={aiSummaryOverride ?? aiSummary}
                                    clientCompanyId={clientCompanyId}
                                    connectionId={connectionId}
                                    onSummaryFetched={setAiSummaryOverride}
                                />
                            )}
                        </div>
                    ) : (
                        blockedBy ? (
                            <CrmSlotTakenNotice
                                headline={crmBlockedHeadline(blockedBy)}
                                detail={crmBlockedDetail(blockedBy, IntegrationProvider.Salesforce)}
                            />
                        ) : (
                            <ConnectView isConnecting={isConnecting} onConnect={connect} assetBase={assetBase} />
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                    <div>
                        {!isLoading && isConnected && (
                            <button
                                type="button"
                                onClick={handleDisconnect}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/30"
                            >
                                <Unplug className="w-4 h-4" /> Disconnect Salesforce
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </>
    )
}

function ConnectView({ isConnecting, onConnect, assetBase }: { isConnecting: boolean; onConnect: () => void; assetBase: string }) {
    return (
        <div className="space-y-8">
            <div className="text-center space-y-3">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Connect to Salesforce</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                    Authorize OrbitalX to access your Salesforce org. This creates a secure OAuth connection at the company level.
                </p>
            </div>
            <div className="flex justify-center">
                <button
                    onClick={onConnect}
                    disabled={isConnecting}
                    className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-[#00a1e0] hover:bg-[#0089bd] text-white font-medium text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                >
                    {isConnecting
                        ? <Loader2 className="w-5 h-5 animate-spin" />
                        : <img src={`${assetBase}/assets/integrations/salesforce-logo.svg`} alt="" className="w-6 h-6" />}
                    {isConnecting ? 'Connecting…' : 'Connect Salesforce'}
                </button>
            </div>
        </div>
    )
}

function ConnectionStatusCard({ instanceUrl, scopes, connectedAtUtc, lastOutboundSyncUtc, lastInboundSyncUtc, healthStatus, isSoftFailure }: {
    instanceUrl: string | null
    scopes: string
    connectedAtUtc: string | null
    lastOutboundSyncUtc: string | null
    lastInboundSyncUtc: string | null
    healthStatus: string
    isSoftFailure: boolean
}) {
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">Connection Status</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Salesforce is connected and active</p>
                    </div>
                </div>
                <span className="text-[10px] px-2.5 py-1 rounded-md font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Connected</span>
            </div>

            <ul className="space-y-2 text-xs border-t border-slate-100 dark:border-slate-800 pt-4">
                {instanceUrl && <SummaryRow label="Instance" value={instanceUrl} />}
                <SummaryRow label="Connected" value={formatDate(connectedAtUtc)} />
                {scopes && <SummaryRow label="Scopes" value={<ScopeList scopes={scopes} />} />}
                <SummaryRow label="Last outbound sync" value={formatSync(lastOutboundSyncUtc)} />
                <SummaryRow label="Last inbound sync" value={formatSync(lastInboundSyncUtc)} />
                {healthStatus && (
                    <SummaryRow
                        label="Sync health"
                        value={<IntegrationStatusBadge status={healthStatus} isSoftFailure={isSoftFailure} />}
                    />
                )}
            </ul>
        </div>
    )
}

function SummaryRow({ label, value }: { label: string; value: ComponentChildren }) {
    return (
        <li className="flex items-start justify-between gap-3">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="text-slate-800 dark:text-slate-200 font-medium text-right break-all">{value}</span>
        </li>
    )
}

// Date only (connected date). Falls back to an em dash when absent/unparseable.
function formatDate(iso?: string | null): string {
    if (!iso) return '—'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

// Date + time for sync timestamps. "Never" reads better than a dash for a sync that hasn't run.
function formatSync(iso?: string | null): string {
    if (!iso) return 'Never'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? 'Never' : d.toLocaleString()
}
